import { useState, useEffect, useMemo } from 'react';
import { Spinner, Alert } from '@components/ui';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  EnvelopeIcon,
  HomeIcon,
  ArrowUturnLeftIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { TONE_SURFACE } from '@components/ui/designTokens';
import { useBranchOffices, useDeleteBranchOffice, useReactivateBranchOffice } from '../hooks/useBranchOffices';
import { BranchOfficeFormModal } from '@/features/companies/components/BranchOfficeFormModal';
import type { BranchOffice } from '../types/branchOffice.types';
import Swal from 'sweetalert2';

/**
 * Sucursales — Claude Design · Sucursales.dc.html
 *
 * Se conservan los mismos datos que exponía la tabla anterior (código, fiscal,
 * nombre, cantidad de almacenes, dirección, coordenadas, email, tipo de
 * dirección, tipo principal/sucursal, estado activa/inactiva, y las acciones
 * editar/desactivar/reactivar); lo único que cambia es la presentación: KPI
 * strip + search + chips de filtro + hero card para la principal + grid de
 * cards secundarias + slot "Añadir nueva". Toda la lógica (búsqueda con
 * debounce, filtrado y paginación client-side, modal, mutations) se mantiene.
 */

type BranchFilter = 'all' | 'main' | 'branch';

export function BranchOfficeManagerPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filter, setFilter] = useState<BranchFilter>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState<BranchOffice | null>(null);

  // Debounce search to avoid losing focus
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  // Reset to page 1 when search or filter change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filter]);

  // Fetch ALL branch offices (large pageSize to get all, without search param)
  const { data: allBranchesData, isLoading, error } = useBranchOffices({
    page: 1,
    pageSize: 9999,
  });
  const deleteMutation = useDeleteBranchOffice();
  const reactivateMutation = useReactivateBranchOffice();

  const allBranches = allBranchesData?.branchOffices ?? [];

  // KPI counts: totals sobre TODAS las sucursales (no filtradas ni paginadas)
  const kpi = useMemo(() => {
    const mainCount = allBranches.filter((b) => b.isMain).length;
    const branchCount = allBranches.filter((b) => !b.isMain).length;
    const warehousesTotal = allBranches.reduce(
      (sum, b) => sum + (b.warehousesCount ?? 0),
      0,
    );
    const branchesWithWarehouses = allBranches.filter(
      (b) => (b.warehousesCount ?? 0) > 0,
    ).length;
    const provinces = Array.from(
      new Set(
        allBranches
          .map((b) => (b.province ?? '').trim())
          .filter((p) => p.length > 0),
      ),
    );
    const cities = new Set(
      allBranches
        .map((b) => (b.city ?? '').trim())
        .filter((c) => c.length > 0),
    );
    return {
      total: allBranches.length,
      mainCount,
      branchCount,
      warehousesTotal,
      branchesWithWarehouses,
      provinces,
      citiesCount: cities.size,
    };
  }, [allBranches]);

  // Client-side filtering (search + tipo) + pagination
  const { paginatedBranches, totalFiltered } = useMemo(() => {
    if (!allBranchesData?.branchOffices) {
      return { paginatedBranches: [], totalFiltered: 0 };
    }

    let filtered = allBranchesData.branchOffices;

    if (filter === 'main') {
      filtered = filtered.filter((b) => b.isMain);
    } else if (filter === 'branch') {
      filtered = filtered.filter((b) => !b.isMain);
    }

    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      filtered = filtered.filter((branch) => {
        const name = branch.name.toLowerCase();
        const code = (branch.code || '').toLowerCase();
        const fiscalCode = (branch.fiscalCode || '').toLowerCase();
        const address = (branch.fullAddress || '').toLowerCase();

        return (
          name.includes(searchLower) ||
          code.includes(searchLower) ||
          fiscalCode.includes(searchLower) ||
          address.includes(searchLower)
        );
      });
    }

    const pageSize = 10;
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginated = filtered.slice(startIndex, endIndex);

    return {
      paginatedBranches: paginated,
      totalFiltered: filtered.length,
    };
  }, [allBranchesData, debouncedSearch, filter, page]);

  const data = useMemo(() => {
    if (!allBranchesData) return undefined;

    const pageSize = 10;
    return {
      branchOffices: paginatedBranches,
      total: totalFiltered,
      page,
      pageSize,
    };
  }, [allBranchesData, paginatedBranches, totalFiltered, page]);

  const handleOpenCreate = () => {
    setEditingBranch(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (branch: BranchOffice) => {
    setEditingBranch(branch);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingBranch(null);
  };

  const handleBranchOfficeCreated = () => {
    // The mutation invalidates the query, so the list refreshes on its own.
  };

  const handleDelete = async (branch: BranchOffice) => {
    if (branch.isMain) {
      Swal.fire({
        title: 'No permitido',
        text: 'No se puede eliminar la sucursal principal',
        icon: 'warning',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: `Se desactivará la sucursal "${branch.name}"`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, desactivar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      await deleteMutation.mutateAsync({ id: branch.id, companyId: branch.companyId });
      Swal.fire({
        title: 'Desactivada',
        text: 'La sucursal ha sido desactivada exitosamente',
        icon: 'success',
        confirmButtonText: 'Aceptar',
      });
    }
  };

  const handleReactivate = async (branch: BranchOffice) => {
    const result = await Swal.fire({
      title: '¿Reactivar sucursal?',
      text: `La sucursal "${branch.name}" volverá a estar activa.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#16a34a',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, reactivar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      await reactivateMutation.mutateAsync({ id: branch.id, companyId: branch.companyId });
      Swal.fire({
        title: 'Reactivada',
        text: 'La sucursal ha sido reactivada exitosamente',
        icon: 'success',
        confirmButtonText: 'Aceptar',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" title="Error al cargar sucursales" data-testid="branch-office-manager-error-alert">
        Ocurrió un error al cargar las sucursales. Por favor, intenta de nuevo.
      </Alert>
    );
  }

  const cardShadow =
    'shadow-[0_1px_2px_rgba(10,34,51,.03),0_10px_24px_-14px_rgba(10,34,51,.14)]';
  const branchShadow =
    'shadow-[0_1px_2px_rgba(10,34,51,.03),0_12px_32px_-18px_rgba(10,34,51,.14)]';
  const chipCountBg = 'bg-[#f4f4f4] text-secondary-500 dark:bg-secondary-700 dark:text-secondary-300';

  const filters: { key: BranchFilter; label: string; count: number }[] = [
    { key: 'all', label: 'Todos', count: kpi.total },
    { key: 'main', label: 'Principal', count: kpi.mainCount },
    { key: 'branch', label: 'Sucursales', count: kpi.branchCount },
  ];

  return (
    <div className="max-w-[1600px]">
      {/* ============= PAGE HEAD ============= */}
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          {/*
            El ultimo segmento del breadcrumb ES el titulo (h1) — sin eyebrow
            arriba (quedaba de mas junto al breadcrumb).
          */}
          <nav className="flex items-baseline gap-2">
            <span className="text-[14px] text-secondary-400">Inicio</span>
            <span className="text-[14px] text-secondary-300">/</span>
            <h1 className="text-2xl font-bold tracking-[-0.03em] text-ink-850 dark:text-white">
              Gestión de Sucursales
            </h1>
          </nav>
          <p className="mt-2 text-[15px] text-secondary-500 dark:text-secondary-400">
            Administra las sucursales de tu empresa
          </p>
        </div>
        <button data-testid="branch-office-manager-new-button"
          type="button"
          onClick={handleOpenCreate}
          className="inline-flex items-center gap-2 rounded-[10px] bg-ink-850 px-[22px] py-3 text-[14px] font-semibold text-white transition-colors hover:bg-[#142d40]"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          Nueva Sucursal
        </button>
      </div>

      {/* ============= KPI STRIP =============
          Se calculan sobre TODAS las sucursales (no las filtradas), así que la
          composición y la cobertura no dependen de la búsqueda actual. */}
      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)]">
        {/* Card 1: Puntos activos + composition */}
        <div className={`rounded-2xl border border-[#ececec] bg-white p-6 ${cardShadow} dark:border-secondary-700 dark:bg-secondary-800`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-secondary-400">
                Puntos activos
              </div>
              <div className="mt-1.5 flex items-baseline gap-2.5">
                <span className="text-[40px] font-extrabold leading-none tracking-[-0.035em] text-ink-850 dark:text-white">
                  {kpi.total}
                </span>
                <span className="text-[14px] text-secondary-500 dark:text-secondary-400">
                  sucursales en{' '}
                  <b className="text-ink-850 dark:text-white">
                    {kpi.provinces.length} {kpi.provinces.length === 1 ? 'provincia' : 'provincias'}
                  </b>
                </span>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex h-2.5 gap-[3px] overflow-hidden rounded-full">
              {kpi.mainCount > 0 && (
                <div
                  className="bg-gradient-to-br from-[#12c4b2] to-[#6b239e]"
                  style={{ flex: kpi.mainCount }}
                  title={`Principal ${kpi.mainCount}`}
                />
              )}
              {kpi.branchCount > 0 && (
                <div
                  className="bg-[#12c4b2]"
                  style={{ flex: kpi.branchCount }}
                  title={`Sucursales ${kpi.branchCount}`}
                />
              )}
              {kpi.total === 0 && <div className="flex-1 bg-secondary-100 dark:bg-secondary-700" />}
            </div>
            <div className="mt-2.5 flex flex-wrap items-center gap-x-5 gap-y-1 text-[13px] text-secondary-500 dark:text-secondary-400">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-sm bg-gradient-to-br from-[#12c4b2] to-[#6b239e]" />
                Principal <b className="text-ink-850 dark:text-white">{kpi.mainCount}</b>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-sm bg-[#12c4b2]" />
                Sucursales <b className="text-ink-850 dark:text-white">{kpi.branchCount}</b>
              </span>
            </div>
          </div>
        </div>

        {/* Card 2: Almacenes totales */}
        <div className={`rounded-2xl border border-[#ececec] bg-white p-6 ${cardShadow} dark:border-secondary-700 dark:bg-secondary-800`}>
          <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-secondary-400">
            Almacenes totales
          </div>
          <div className="mt-2.5 flex items-center gap-3.5">
            <span className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[11px] bg-[rgba(107,35,158,0.10)] text-[#6b239e]">
              <HomeIcon className="h-5 w-5" />
            </span>
            <div>
              <div className="text-[32px] font-extrabold leading-none tracking-[-0.03em] text-ink-850 dark:text-white">
                {kpi.warehousesTotal}
              </div>
              <div className="mt-0.5 text-[13px] text-secondary-500 dark:text-secondary-400">
                distribuidos en {kpi.branchesWithWarehouses}{' '}
                {kpi.branchesWithWarehouses === 1 ? 'sucursal' : 'sucursales'}
              </div>
            </div>
          </div>
        </div>

        {/* Card 3: Cobertura */}
        <div className={`rounded-2xl border border-[#ececec] bg-white p-6 ${cardShadow} dark:border-secondary-700 dark:bg-secondary-800`}>
          <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-secondary-400">
            Cobertura
          </div>
          <div className="mt-2.5 flex items-center gap-3">
            <span className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[11px] bg-accent-500/10 text-[#0d9488]">
              <MapPinIcon className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <div className="truncate text-[15px] font-bold text-ink-850 dark:text-white">
                {kpi.provinces.length > 0 ? kpi.provinces.join(' · ') : 'Sin cobertura registrada'}
              </div>
              <div className="mt-1 font-mono text-[12px] uppercase text-secondary-400">
                {kpi.provinces.length} {kpi.provinces.length === 1 ? 'PROVINCIA' : 'PROVINCIAS'}
                {kpi.citiesCount > 0 && ` · ${kpi.citiesCount} ${kpi.citiesCount === 1 ? 'CIUDAD' : 'CIUDADES'}`}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============= SEARCH + FILTER CHIPS ============= */}
      <div className="mt-6 grid grid-cols-1 items-center gap-4 md:grid-cols-[minmax(0,1fr)_auto]">
        <div className="relative rounded-xl border border-[#ececec] bg-white transition focus-within:border-[#0d9488] focus-within:shadow-[0_0_0_3px_rgba(18,196,178,0.10)] dark:border-secondary-700 dark:bg-secondary-800">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
          <input
            type="text"
            placeholder="Buscar por nombre o código…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="branch-office-manager-search-input"
            className="w-full rounded-xl border-0 bg-transparent py-3 pl-11 pr-3 text-[15px] text-ink-850 outline-none placeholder:text-[#a1a1aa] focus:ring-0 dark:text-white"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {filters.map((f) => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                aria-pressed={active}
                className={
                  'inline-flex items-center gap-2 whitespace-nowrap rounded-full border px-3.5 py-2 text-[13px] font-semibold transition-all ' +
                  (active
                    ? 'border-ink-850 bg-ink-850 text-white'
                    : 'border-[#ececec] bg-white text-secondary-500 hover:border-secondary-300 hover:text-ink-850 dark:border-secondary-700 dark:bg-secondary-800 dark:text-secondary-300 dark:hover:text-white')
                }
              >
                {f.label}
                <span
                  className={
                    'font-mono text-[12px] font-bold rounded-full px-1.5 py-[1px] ' +
                    (active ? 'bg-white/[.14] text-white' : chipCountBg)
                  }
                >
                  {f.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {search !== debouncedSearch && search.length > 0 && (
        <p className="mt-2 flex items-center gap-1 text-xs text-secondary-500 dark:text-secondary-400">
          <Spinner size="sm" />
          Buscando...
        </p>
      )}

      {/* ============= EMPTY / RESULTS ============= */}
      {data && data.branchOffices.length === 0 ? (
        <div className={`mt-6 rounded-2xl border border-[#ececec] bg-white px-6 py-14 text-center ${cardShadow} dark:border-secondary-700 dark:bg-secondary-800`}>
          <BuildingOfficeIcon className="mx-auto h-12 w-12 text-secondary-300" />
          <h3 className="mt-3 text-[15px] font-bold text-ink-850 dark:text-white">
            No hay sucursales
          </h3>
          <p className="mt-1 text-[14px] text-secondary-500 dark:text-secondary-400">
            {debouncedSearch
              ? `No se encontraron resultados para "${debouncedSearch}"`
              : filter !== 'all'
                ? 'No hay sucursales que coincidan con este filtro'
                : 'Comienza creando tu primera sucursal'}
          </p>
          {!debouncedSearch && filter === 'all' && (
            <button data-testid="branch-office-manager-empty-new-button"
              type="button"
              onClick={handleOpenCreate}
              className="mt-5 inline-flex items-center gap-2 rounded-[10px] bg-ink-850 px-[22px] py-3 text-[14px] font-semibold text-white transition-colors hover:bg-[#142d40]"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Nueva Sucursal
            </button>
          )}
        </div>
      ) : (
        <>
          {/* ============= HERO PRINCIPAL BRANCH (si está en la página actual) ============= */}
          {(() => {
            const principal = data?.branchOffices.find((b) => b.isMain);
            if (!principal) return null;
            return (
              <div
                className={`relative mt-5 overflow-hidden rounded-2xl border ${principal.isActive ? 'border-teal-500/25' : 'border-danger-200'} p-7 ${branchShadow} ${principal.isActive ? TONE_SURFACE.teal : TONE_SURFACE.red} transition-[transform,border-color,box-shadow] hover:-translate-y-[1px] hover:border-teal-500/50 hover:shadow-[0_1px_2px_rgba(10,34,51,.04),0_20px_44px_-20px_rgba(10,34,51,.20)] dark:border-secondary-700`}
              >
                <span className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-[#12c4b2] to-[#6b239e]" />
                <div className="grid grid-cols-1 items-center gap-7 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto]">
                  {/* Identity */}
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(107,35,158,0.24)] bg-gradient-to-br from-[rgba(18,196,178,0.14)] to-[rgba(107,35,158,0.14)] px-2.5 py-1 text-[12px] font-semibold text-ink-850 dark:text-white">
                        <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-br from-[#12c4b2] to-[#6b239e]" />
                        SEDE PRINCIPAL
                      </span>
                      <span className="font-mono text-[12px] uppercase tracking-[0.06em] text-secondary-400">
                        CÓDIGO <b className="font-bold text-ink-850 dark:text-white">{principal.code}</b>
                        {principal.fiscalCode && (
                          <>
                            {' '}
                            · FISCAL <b className="font-bold text-ink-850 dark:text-white">{principal.fiscalCode}</b>
                          </>
                        )}
                      </span>
                      {!principal.isActive && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-danger-50 px-2.5 py-1 text-[12px] font-semibold text-danger-600 dark:bg-danger-900/30 dark:text-danger-300">
                          Inactiva
                        </span>
                      )}
                      {principal.addressType && (
                        <span className="rounded-full border border-[#ececec] bg-white px-2.5 py-1 text-[12px] font-semibold text-secondary-500 dark:border-secondary-700 dark:bg-secondary-900 dark:text-secondary-300">
                          {principal.addressType}
                        </span>
                      )}
                    </div>
                    <div className="mt-2.5 text-[26px] font-extrabold leading-tight tracking-[-0.025em] text-ink-850 dark:text-white">
                      {principal.name}
                    </div>
                    {principal.fullAddress ? (
                      <>
                        <div className="mt-2 flex items-start gap-1.5 text-secondary-500 dark:text-secondary-400">
                          <MapPinIcon className="mt-[3px] h-3.5 w-3.5 flex-shrink-0" />
                          <span className="text-[14px]">{principal.fullAddress}</span>
                        </div>
                        {principal.coordinates?.latitude != null && principal.coordinates?.longitude != null && (
                          <div className="mt-1.5 pl-5 font-mono text-[12px] text-secondary-400">
                            {principal.coordinates.latitude.toFixed(6)}, {principal.coordinates.longitude.toFixed(6)}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="mt-2 pl-5 text-[14px] text-secondary-400">Sin dirección</div>
                    )}
                  </div>

                  {/* Contact + almacenes */}
                  <div className="border-l border-dashed border-secondary-200 pl-6 dark:border-secondary-700">
                    <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.20em] text-secondary-400">
                      Contacto
                    </div>
                    {principal.email ? (
                      <div className="flex items-center gap-2 font-mono text-[14px] font-semibold text-ink-850 dark:text-white">
                        <EnvelopeIcon className="h-3.5 w-3.5 flex-shrink-0 text-secondary-400" />
                        <span className="truncate">{principal.email}</span>
                      </div>
                    ) : (
                      <div className="text-[14px] text-secondary-400">Sin contacto</div>
                    )}
                    {(principal.warehousesCount ?? 0) > 0 && (
                      <div className="mt-3.5">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(107,35,158,0.08)] px-2.5 py-1 font-mono text-[12px] font-bold uppercase tracking-[0.06em] text-[#6b239e]">
                          <span className="h-1.5 w-1.5 rounded-full bg-[#6b239e]" />
                          {principal.warehousesCount} {principal.warehousesCount === 1 ? 'ALMACÉN' : 'ALMACENES'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="inline-flex items-center gap-1.5">
                    {principal.isActive ? (
                      <>
                        <button
                          type="button"
                          onClick={() => handleOpenEdit(principal)}
                          className="inline-flex items-center gap-2 rounded-[10px] border border-[#ececec] bg-white px-3.5 py-2.5 text-[14px] font-medium text-ink-850 transition hover:border-secondary-300 dark:border-secondary-700 dark:bg-secondary-900 dark:text-white"
                        >
                          <PencilIcon className="h-3.5 w-3.5" />
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(principal)}
                          disabled
                          title="No se puede eliminar la sucursal principal"
                          className="inline-flex h-8 w-8 cursor-not-allowed items-center justify-center rounded-lg border border-transparent text-secondary-400 opacity-40"
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleReactivate(principal)}
                        title="Reactivar sucursal"
                        className="inline-flex items-center gap-2 rounded-[10px] border border-success-200 bg-success-50 px-3.5 py-2.5 text-[14px] font-medium text-success-700 transition hover:bg-success-100 dark:border-success-700 dark:bg-success-900/30 dark:text-success-200"
                      >
                        <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                        Reactivar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ============= SECONDARY BRANCH GRID =============
              Grid con las sucursales no-principales de la página actual + slot
              "Añadir nueva sucursal" solo cuando estamos viendo la primera
              página completa (no en filtros ni búsqueda). */}
          {(() => {
            const secondary = (data?.branchOffices ?? []).filter((b) => !b.isMain);
            const showAddSlot =
              filter !== 'main' && !debouncedSearch && page === 1 && kpi.branchCount === secondary.length;
            return (
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="branch-office-manager-table">
                {secondary.map((branch) => (
                  <div
                    data-testid={`branch-office-manager-row-${branch.id}`}
                    key={branch.id}
                    className={
                      /*
                        Resalte de seleccion al pasar el mouse, igual criterio que
                        ROW_HOVER en los listados: el tinte teal es lo que dice
                        "esta es la que estas mirando". Antes solo cambiaba el
                        borde y no se percibia.
                      */
                      'relative overflow-hidden rounded-2xl border p-4 transition-[transform,border-color,box-shadow] hover:-translate-y-[1px] ' +
                      (branch.isActive ? TONE_SURFACE.teal : TONE_SURFACE.red) +
                      ' ' +
                      branchShadow +
                      ' ' +
                      (branch.isActive
                        ? 'border-teal-500/25 hover:border-teal-500/50 dark:border-secondary-700'
                        : 'border-danger-200 dark:border-danger-700/60')
                    }
                  >
                    <span
                      className={
                        'absolute inset-y-0 left-0 w-1 ' +
                        (branch.isActive ? 'bg-[#12c4b2]' : 'bg-danger-400')
                      }
                    />
                    {/* Header */}
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <span
                          className={
                            'inline-flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-[10px] ' +
                            (branch.isActive
                              ? 'bg-accent-500/10 text-[#0d9488]'
                              : 'bg-secondary-100 text-secondary-400 dark:bg-secondary-700')
                          }
                        >
                          <BuildingOfficeIcon className="h-4 w-4" />
                        </span>
                        <div className="min-w-0">
                          <div className="font-mono text-[12px] uppercase tracking-[0.06em] text-secondary-400">
                            {branch.code}
                            {branch.fiscalCode && ` · FISCAL ${branch.fiscalCode}`}
                          </div>
                          <div className="mt-0.5 truncate text-[16px] font-bold tracking-[-0.01em] text-ink-850 dark:text-white">
                            {branch.name}
                          </div>
                        </div>
                      </div>
                      {/* Las pastillas envuelven en linea en vez de apilarse:
                          apiladas sumaban tres renglones y estiraban la tarjeta
                          casi al doble de alto sin aportar nada. */}
                      <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-1.5">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500/10 px-2.5 py-1 text-[12px] font-semibold text-[#0d9488]">
                          <span className="h-1.5 w-1.5 rounded-full bg-[#12c4b2]" />
                          Sucursal
                        </span>
                        {!branch.isActive && (
                          <span className="rounded-full bg-danger-50 px-2.5 py-1 text-[12px] font-semibold text-danger-600 dark:bg-danger-900/30 dark:text-danger-300">
                            Inactiva
                          </span>
                        )}
                        {branch.addressType && (
                          <span className="rounded-full border border-[#ececec] bg-white px-2 py-[2px] text-[11px] font-semibold text-secondary-500 dark:border-secondary-700 dark:bg-secondary-900 dark:text-secondary-300">
                            {branch.addressType}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Address */}
                    <div className="mt-3 border-t border-dashed border-[#eef0f2] pt-3 dark:border-secondary-700">
                      {branch.fullAddress ? (
                        <>
                          <div className="flex items-start gap-1.5 text-ink-850 dark:text-white">
                            <MapPinIcon className="mt-[3px] h-3.5 w-3.5 flex-shrink-0 text-secondary-400" />
                            <span className="text-[14px]">{branch.fullAddress}</span>
                          </div>
                          {branch.coordinates?.latitude != null && branch.coordinates?.longitude != null && (
                            <div className="mt-1.5 pl-5 font-mono text-[12px] text-secondary-400">
                              {branch.coordinates.latitude.toFixed(6)}, {branch.coordinates.longitude.toFixed(6)}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-[14px] text-secondary-400">Sin dirección</div>
                      )}

                      {/* Contact row: email + almacenes */}
                      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2.5">
                        <div className="flex min-w-0 flex-1 items-center gap-2 font-mono text-[13px] text-ink-850 dark:text-white">
                          <EnvelopeIcon className="h-3 w-3 flex-shrink-0 text-secondary-400" />
                          <span className="truncate">
                            {branch.email || <span className="text-secondary-400">Sin contacto</span>}
                          </span>
                        </div>
                        {(branch.warehousesCount ?? 0) > 0 && (
                          <span className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-[rgba(107,35,158,0.08)] px-2.5 py-1 font-mono text-[12px] font-bold uppercase tracking-[0.06em] text-[#6b239e]">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#6b239e]" />
                            {branch.warehousesCount} {branch.warehousesCount === 1 ? 'ALMACÉN' : 'ALMACENES'}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-2.5 flex items-center justify-end gap-1 border-t border-dashed border-[#eef0f2] pt-2.5 dark:border-secondary-700">
                      {branch.isActive ? (
                        <>
                          <button
                            type="button"
                            onClick={() => handleOpenEdit(branch)}
                            data-testid={`branch-office-manager-edit-button-${branch.id}`}
                            aria-label="Editar sucursal"
                            title="Editar sucursal"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-transparent text-secondary-400 transition hover:border-[#ececec] hover:bg-secondary-50 hover:text-ink-850 dark:hover:border-secondary-700 dark:hover:bg-secondary-900 dark:hover:text-white"
                          >
                            <PencilIcon className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(branch)}
                            data-testid={`branch-office-manager-delete-button-${branch.id}`}
                            aria-label="Desactivar sucursal"
                            title="Desactivar sucursal"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-transparent text-secondary-400 transition hover:border-[rgba(184,80,58,0.20)] hover:bg-[rgba(184,80,58,0.06)] hover:text-[#b8503a]"
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleReactivate(branch)}
                          data-testid={`branch-office-manager-reactivate-button-${branch.id}`}
                          aria-label="Reactivar sucursal"
                          title="Reactivar sucursal"
                          className="inline-flex items-center gap-1.5 rounded-lg border border-success-200 bg-success-50 px-2.5 py-1.5 text-[13px] font-semibold text-success-700 transition hover:bg-success-100 dark:border-success-700 dark:bg-success-900/30 dark:text-success-200"
                        >
                          <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                          Reactivar
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {showAddSlot && (
                  <button
                    type="button"
                    onClick={handleOpenCreate}
                    className="group flex flex-col items-center justify-center rounded-2xl border-[1.5px] border-dashed border-secondary-300 bg-transparent px-6 py-8 text-center text-secondary-500 transition-all hover:border-[#12c4b2] hover:bg-[rgba(18,196,178,0.03)] hover:text-[#0d9488] dark:border-secondary-600 dark:text-secondary-400"
                  >
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-[10px] border border-[#ececec] bg-white text-[#0d9488] dark:border-secondary-700 dark:bg-secondary-800">
                      <PlusIcon className="h-4 w-4" />
                    </span>
                    <div className="mt-2.5 text-[15px] font-bold text-ink-850 dark:text-white">
                      Añadir nueva sucursal
                    </div>
                    <div className="mt-1 text-[13px] text-secondary-400">
                      Expande tu red comercial
                    </div>
                  </button>
                )}
              </div>
            );
          })()}

          {/* ============= PAGINATION ============= */}
          {data && data.total > 0 && (
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-[#f0f0f0] px-1 py-4 dark:border-secondary-700">
              <div className="text-[13px] text-secondary-500 dark:text-secondary-400">
                Mostrando{' '}
                <span className="font-bold text-ink-850 dark:text-white">
                  {(page - 1) * data.pageSize + 1}
                </span>{' '}
                a{' '}
                <span className="font-bold text-ink-850 dark:text-white">
                  {Math.min(page * data.pageSize, data.total)}
                </span>{' '}
                de{' '}
                <span className="font-bold text-ink-850 dark:text-white">{data.total}</span>{' '}
                {data.total === 1 ? 'sucursal' : 'sucursales'}
                <span className="mx-2.5 text-secondary-300">·</span>
                Página{' '}
                <span className="font-bold text-ink-850 dark:text-white">{page}</span> de{' '}
                <span className="font-bold text-ink-850 dark:text-white">
                  {Math.max(1, Math.ceil(data.total / data.pageSize))}
                </span>
              </div>
              <div className="inline-flex gap-2">
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  data-testid="branch-office-manager-prev-page-button"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[#ececec] bg-white px-3.5 py-2 text-[14px] font-medium text-ink-850 transition disabled:cursor-not-allowed disabled:text-secondary-300 hover:enabled:border-secondary-300 dark:border-secondary-700 dark:bg-secondary-800 dark:text-white"
                >
                  <ChevronLeftIcon className="h-3 w-3" />
                  Anterior
                </button>
                <button
                  type="button"
                  disabled={page * data.pageSize >= data.total}
                  onClick={() => setPage(page + 1)}
                  data-testid="branch-office-manager-next-page-button"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[#ececec] bg-white px-3.5 py-2 text-[14px] font-medium text-ink-850 transition disabled:cursor-not-allowed disabled:text-secondary-300 hover:enabled:border-secondary-300 dark:border-secondary-700 dark:bg-secondary-800 dark:text-white"
                >
                  Siguiente
                  <ChevronRightIcon className="h-3 w-3" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal Create/Edit Branch Office */}
      <BranchOfficeFormModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onBranchOfficeCreated={handleBranchOfficeCreated}
        branchOffice={editingBranch}
      />
    </div>
  );
}
